/* Command Center — Poste de pilotage patrimonial
 *
 * Une page unique avec sous-onglets. Responsive mobile-first : les grilles
 * s'adaptent (1 col sur mobile → 2/3/4 col sur desktop) et la nav interne
 * scrolle horizontalement sur petit écran.
 *
 * Ne remplace AUCUN module existant : ce n'est qu'une vue transverse.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import KpiCard from "@/components/common/KpiCard";
import StatusBadge from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
  DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  LayoutDashboard, Target, Bell, Activity, TrendingUp, Scale, Lightbulb,
  Download, RefreshCw, Trash2, CheckCircle2, AlertTriangle, XCircle,
  Building2, Home, ArrowUp, ArrowDown, ChevronRight, FileSpreadsheet,
  FileText, Sparkles, Gauge,
} from "lucide-react";
import SmartLoader from "@/components/common/SmartLoader";

/* =========================================================================
   Helpers UI
   ========================================================================= */

const eur = (v) => {
  const n = Number(v || 0);
  return `${n.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €`;
};
const pct = (v) => `${Number(v || 0).toFixed(1)} %`;

const SEV_TONE = { info: "info", warning: "warning", critical: "danger" };
const PRIO_TONE = { high: "danger", medium: "warning", low: "info" };
const PRIO_LABEL = { high: "Haute", medium: "Moyenne", low: "Faible" };

function Gauge3D({ value, target, unit = "EUR", tone = "success" }) {
  const pctVal = Math.min(100, Math.max(0, Number(value) || 0));
  const color =
    pctVal >= 100 ? "bg-emerald-500" :
      pctVal >= 80 ? "bg-emerald-400" :
        pctVal >= 50 ? "bg-amber-400" : "bg-red-400";
  return (
    <div className="space-y-1">
      <div className="h-2.5 w-full bg-secondary rounded-full overflow-hidden">
        <div className={`${color} h-full transition-all`}
             style={{ width: `${pctVal}%` }} />
      </div>
      <div className="flex justify-between text-[10px] text-muted-foreground uppercase tracking-widest">
        <span>{pctVal.toFixed(0)} % de l&apos;objectif</span>
        <span>Cible {unit === "EUR" ? eur(target) : pct(target)}</span>
      </div>
    </div>
  );
}

/* =========================================================================
   Page principale
   ========================================================================= */

export default function CommandCenterPage() {
  const [tab, setTab] = useState("overview");
  return (
    <div className="animate-fade-up" data-testid="command-center-page">
      <PageHeader
        overline="Centre de pilotage"
        title="Poste de commandement patrimonial"
        description="Une vue unique et actionnable de vos SCI, immeubles, loyers, crédits et objectifs — avec alertes, recommandations et score de santé."
      />
      <Tabs value={tab} onValueChange={setTab}>
        {/* Nav horizontale scrollable sur mobile */}
        <div className="-mx-4 md:mx-0 mb-6 overflow-x-auto scrollbar-none">
          <TabsList className="w-max md:w-auto px-4 md:px-0 h-auto flex-nowrap">
            <TabsTrigger value="overview" data-testid="cc-tab-overview">
              <LayoutDashboard className="h-3.5 w-3.5 mr-1.5" />Vue d&apos;ensemble
            </TabsTrigger>
            <TabsTrigger value="objectives" data-testid="cc-tab-objectives">
              <Target className="h-3.5 w-3.5 mr-1.5" />Objectifs
            </TabsTrigger>
            <TabsTrigger value="alerts" data-testid="cc-tab-alerts">
              <Bell className="h-3.5 w-3.5 mr-1.5" />Alertes
            </TabsTrigger>
            <TabsTrigger value="health" data-testid="cc-tab-health">
              <Activity className="h-3.5 w-3.5 mr-1.5" />Santé
            </TabsTrigger>
            <TabsTrigger value="advisor" data-testid="cc-tab-advisor">
              <Lightbulb className="h-3.5 w-3.5 mr-1.5" />Conseils
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="cc-tab-history">
              <TrendingUp className="h-3.5 w-3.5 mr-1.5" />Historique
            </TabsTrigger>
            <TabsTrigger value="compare" data-testid="cc-tab-compare">
              <Scale className="h-3.5 w-3.5 mr-1.5" />Comparer
            </TabsTrigger>
            <TabsTrigger value="export" data-testid="cc-tab-export">
              <Download className="h-3.5 w-3.5 mr-1.5" />Export
            </TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="overview"><OverviewTab /></TabsContent>
        <TabsContent value="objectives"><ObjectivesTab /></TabsContent>
        <TabsContent value="alerts"><AlertsTab /></TabsContent>
        <TabsContent value="health"><HealthTab /></TabsContent>
        <TabsContent value="advisor"><AdvisorTab /></TabsContent>
        <TabsContent value="history"><HistoryTab /></TabsContent>
        <TabsContent value="compare"><CompareTab /></TabsContent>
        <TabsContent value="export"><ExportTab /></TabsContent>
      </Tabs>
    </div>
  );
}

/* =========================================================================
   TAB 1 — Vue d&apos;ensemble
   ========================================================================= */

function OverviewTab() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ company_id: "", building_id: "" });
  const [links, setLinks] = useState({ companies: [], buildings: [] });
  const [drill, setDrill] = useState(null); // { kind, id }

  const load = async () => {
    setError(null);
    const params = {};
    if (filters.company_id) params.company_id = filters.company_id;
    if (filters.building_id) params.building_id = filters.building_id;
    try {
      const { data } = await api.get("/command-center/overview", { params });
      setData(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Impossible de charger la vue d'ensemble.");
    }
  };

  useEffect(() => {
    Promise.all([api.get("/companies"), api.get("/buildings")])
      .then(([c, b]) => setLinks({
        companies: c.data.items || [], buildings: b.data.items || [],
      }))
      .catch(() => {});
  }, []);
  useEffect(() => { load();  }, [filters]);

  if (!data || error) return <SmartLoader label="Chargement du poste de commandement…" onRetry={load} error={error} />;
  const k = data.kpis;

  return (
    <div className="space-y-6">
      {/* Filtres */}
      <div className="card-elevated p-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-xs">Filtrer par société</Label>
          <Select value={filters.company_id || "__all"}
                  onValueChange={(v) => setFilters({ company_id: v === "__all" ? "" : v, building_id: "" })}>
            <SelectTrigger data-testid="cc-filter-company">
              <SelectValue placeholder="Toutes les sociétés" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">Toutes les sociétés</SelectItem>
              {links.companies.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.denomination || c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <Label className="text-xs">Filtrer par immeuble</Label>
          <Select value={filters.building_id || "__all"}
                  onValueChange={(v) => setFilters({ ...filters, building_id: v === "__all" ? "" : v })}>
            <SelectTrigger data-testid="cc-filter-building">
              <SelectValue placeholder="Tous les immeubles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">Tous les immeubles</SelectItem>
              {links.buildings
                .filter((b) => !filters.company_id || b.company_id === filters.company_id)
                .map((b) => (
                  <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPIs — Patrimoine */}
      <section>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
          Patrimoine
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <KpiCard name="cc-value" label="Valeur estimée"
                    value={eur(k.patrimoine_value)} tone="accent" icon={Building2} />
          <KpiCard name="cc-nw" label="Patrimoine net"
                    value={eur(k.net_worth)} icon={TrendingUp} />
          <KpiCard name="cc-debt" label="Capital restant dû"
                    value={eur(k.remaining_debt)} icon={ArrowDown} />
          <KpiCard name="cc-sci" label="Sociétés / SCI"
                    value={`${k.companies_count} / ${k.sci_count}`} icon={Building2} />
          <KpiCard name="cc-buildings" label="Immeubles / Lots"
                    value={`${k.buildings_count} / ${k.lots_count}`} icon={Home} />
        </div>
      </section>

      {/* KPIs — Locatif */}
      <section>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
          Locatif
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <KpiCard name="cc-paid" label="Loyers encaissés (mois)"
                    value={eur(k.rent_paid_this_month)} tone="accent" />
          <KpiCard name="cc-pending" label="Loyers en attente (mois)"
                    value={eur(k.rent_pending_this_month)} />
          <KpiCard name="cc-unpaid" label="Impayés cumulés"
                    value={eur(k.unpaid_total)} tone={k.unpaid_total > 0 ? "danger" : "neutral"} />
          <KpiCard name="cc-occ" label="Taux d'occupation"
                    value={pct(k.occupancy_pct)} />
          <KpiCard name="cc-tenants" label="Locataires"
                    value={k.tenants_count} />
        </div>
      </section>

      {/* KPIs — Finance */}
      <section>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
          Finance
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <KpiCard name="cc-cf-m" label="Cash-flow mensuel"
                    value={eur(k.cashflow_monthly)}
                    tone={k.cashflow_monthly >= 0 ? "success" : "danger"} />
          <KpiCard name="cc-cf-a" label="Cash-flow annuel"
                    value={eur(k.cashflow_annual)}
                    tone={k.cashflow_annual >= 0 ? "success" : "danger"} />
          <KpiCard name="cc-yield-g" label="Rentabilité brute"
                    value={pct(k.avg_gross_yield_pct)} />
          <KpiCard name="cc-yield-n" label="Rentabilité nette"
                    value={pct(k.avg_net_yield_pct)} />
          <KpiCard name="cc-mensualite" label="Mensualités crédits"
                    value={eur(k.monthly_credit_payment)} />
        </div>
      </section>

      {/* KPIs — Vigilance */}
      <section>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
          Vigilance
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <KpiCard name="cc-vac" label="Vacance"
                    value={pct(k.vacancy_pct)}
                    tone={k.vacancy_pct > 10 ? "warning" : "neutral"} />
          <KpiCard name="cc-missing" label="Docs manquants"
                    value={k.missing_documents_count}
                    tone={k.missing_documents_count > 0 ? "warning" : "success"} />
          <KpiCard name="cc-alerts" label="Alertes actives"
                    value={k.active_alerts_count}
                    tone={k.active_alerts_count > 0 ? "danger" : "success"}
                    icon={Bell} />
        </div>
      </section>

      {/* Drill-in par société / immeuble */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card-elevated p-5">
          <div className="font-heading text-base mb-3 flex items-center justify-between">
            <span>Vos sociétés</span>
            <span className="text-xs text-muted-foreground">{links.companies.length}</span>
          </div>
          <div className="space-y-1 max-h-72 overflow-y-auto">
            {links.companies.length === 0 && <div className="text-sm text-muted-foreground">—</div>}
            {links.companies.map((c) => (
              <button key={c.id}
                      onClick={() => setDrill({ kind: "company", id: c.id })}
                      className="w-full flex justify-between items-center px-3 py-2 rounded-lg hover:bg-secondary/60 text-left"
                      data-testid={`cc-drill-company-${c.id}`}>
                <div>
                  <div className="text-sm font-medium">{c.denomination || c.name}</div>
                  <div className="text-xs text-muted-foreground">{c.legal_form || ""}</div>
                </div>
                <ChevronRight className="h-3 w-3 text-muted-foreground" />
              </button>
            ))}
          </div>
        </div>
        <div className="card-elevated p-5">
          <div className="font-heading text-base mb-3 flex items-center justify-between">
            <span>Vos immeubles</span>
            <span className="text-xs text-muted-foreground">{links.buildings.length}</span>
          </div>
          <div className="space-y-1 max-h-72 overflow-y-auto">
            {links.buildings.length === 0 && <div className="text-sm text-muted-foreground">—</div>}
            {links.buildings.map((b) => (
              <button key={b.id}
                      onClick={() => setDrill({ kind: "building", id: b.id })}
                      className="w-full flex justify-between items-center px-3 py-2 rounded-lg hover:bg-secondary/60 text-left"
                      data-testid={`cc-drill-building-${b.id}`}>
                <div>
                  <div className="text-sm font-medium">{b.name}</div>
                  <div className="text-xs text-muted-foreground">{b.address?.city || ""}</div>
                </div>
                <ChevronRight className="h-3 w-3 text-muted-foreground" />
              </button>
            ))}
          </div>
        </div>
      </section>

      {drill && <DrillDialog drill={drill} onClose={() => setDrill(null)} />}
    </div>
  );
}

function DrillDialog({ drill, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    const url = drill.kind === "company"
      ? `/command-center/companies/${drill.id}`
      : `/command-center/buildings/${drill.id}`;
    api.get(url).then((r) => setData(r.data));
  }, [drill]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>
            {data ? (drill.kind === "company" ? data.company?.name : data.building?.name) : "Chargement…"}
          </DialogTitle>
        </DialogHeader>
        {data && drill.kind === "company" && <CompanyDetail data={data} />}
        {data && drill.kind === "building" && <BuildingDetail data={data} />}
      </DialogContent>
    </Dialog>
  );
}

function CompanyDetail({ data }) {
  return (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MiniKpi label="Patrimoine" value={eur(data.patrimoine_value)} />
        <MiniKpi label="Revenus locatifs" value={eur(data.rental_revenue)} />
        <MiniKpi label="Charges 12 mois" value={eur(data.charges_last_12m)} />
        <MiniKpi label="Résultat estimé" value={eur(data.result_estimate)} />
        <MiniKpi label="Capital restant dû" value={eur(data.credit?.remaining_principal)} />
        <MiniKpi label="Mensualité crédits" value={eur(data.credit?.monthly_payment_total)} />
        <MiniKpi label="Rendement brut" value={pct(data.yields_pct?.gross)} />
        <MiniKpi label="Rendement net" value={pct(data.yields_pct?.net)} />
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Immeubles rattachés</div>
        <div className="rounded-xl border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 text-xs uppercase text-muted-foreground">
              <tr className="text-left">
                <th className="px-3 py-2">Immeuble</th>
                <th className="px-3 py-2 text-right">Valeur</th>
                <th className="px-3 py-2 text-right">Cash-flow/m</th>
                <th className="px-3 py-2 text-right">Rendement net</th>
                <th className="px-3 py-2 text-right">CRD</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {(data.buildings || []).map((b) => (
                <tr key={b.building_id}>
                  <td className="px-3 py-2 font-medium">{b.building_name}</td>
                  <td className="px-3 py-2 text-right font-mono">{eur(b.current_value)}</td>
                  <td className="px-3 py-2 text-right font-mono">{eur(b.cashflow_monthly)}</td>
                  <td className="px-3 py-2 text-right font-mono">{pct(b.net_yield_pct)}</td>
                  <td className="px-3 py-2 text-right font-mono">{eur(b.remaining_debt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function BuildingDetail({ data }) {
  const p = data.perf;
  return (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MiniKpi label="Loyers annuels" value={eur(p.rents.actual_annual)} />
        <MiniKpi label="Charges annuelles" value={eur(p.charges.annual_total)} />
        <MiniKpi label="Crédit annuel" value={eur(p.credit.annual_payment_total)} />
        <MiniKpi label="Cash-flow annuel" value={eur(p.cashflow.annual)}
                  tone={p.cashflow.annual >= 0 ? "success" : "danger"} />
        <MiniKpi label="Rendement brut" value={pct(p.yields_pct.gross)} />
        <MiniKpi label="Rendement net" value={pct(p.yields_pct.net)} />
        <MiniKpi label="Rendement net-net" value={pct(p.yields_pct.net_net)} />
        <MiniKpi label="Occupation" value={pct(p.occupancy_pct)} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TrendCard title="Charges (12 mois)" series={data.trend_charges_12m} suffix="€" />
        <TrendCard title="Loyers encaissés (12 mois)" series={data.trend_rents_12m} suffix="€" />
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Historique travaux / entretien</div>
        {(data.works_history || []).length === 0 ? (
          <div className="text-sm text-muted-foreground">Aucun travail enregistré.</div>
        ) : (
          <div className="rounded-xl border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary/40 text-xs uppercase text-muted-foreground">
                <tr className="text-left"><th className="px-3 py-2">Date</th><th className="px-3 py-2">Fournisseur</th><th className="px-3 py-2">Catégorie</th><th className="px-3 py-2 text-right">Montant</th></tr>
              </thead>
              <tbody className="divide-y">
                {data.works_history.map((w) => (
                  <tr key={w.id}><td className="px-3 py-2">{w.date}</td><td className="px-3 py-2">{w.supplier || "-"}</td><td className="px-3 py-2">{w.category}</td><td className="px-3 py-2 text-right font-mono">{eur(w.amount)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {["water", "elec", "gas"].map((et) => (
          <div key={et} className="card-elevated p-4">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
              Consommation {et === "water" ? "eau" : et === "elec" ? "électricité" : "gaz"}
            </div>
            {(data.consumption?.[et] || []).length === 0 ? (
              <div className="text-xs text-muted-foreground">Aucun relevé</div>
            ) : (
              <div className="space-y-1 text-xs">
                {data.consumption[et].slice(0, 5).map((r) => (
                  <div key={`${et}-${r.period_start}-${r.period_end}`} className="flex justify-between">
                    <span className="text-muted-foreground">{r.period_start} → {r.period_end}</span>
                    <span className="font-mono">{r.consumption}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function MiniKpi({ label, value, tone }) {
  return (
    <div className="rounded-xl bg-secondary/40 p-3">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`font-mono font-semibold mt-1 ${tone === "danger" ? "text-red-600" : tone === "success" ? "text-emerald-700" : ""}`}>
        {value ?? "—"}
      </div>
    </div>
  );
}

function TrendCard({ title, series, suffix = "" }) {
  const max = Math.max(...(series || []).map((s) => s.total || 0), 1);
  return (
    <div className="card-elevated p-4">
      <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">{title}</div>
      {(series || []).length === 0 ? (
        <div className="text-xs text-muted-foreground">Aucune donnée</div>
      ) : (
        <div className="space-y-1">
          {series.map((s) => (
            <div key={s.period} className="flex items-center gap-2 text-xs">
              <span className="w-16 text-muted-foreground">{s.period}</span>
              <div className="flex-1 bg-secondary rounded h-2 overflow-hidden">
                <div className="bg-primary h-full" style={{ width: `${(s.total / max) * 100}%` }} />
              </div>
              <span className="w-20 text-right font-mono">{Math.round(s.total).toLocaleString("fr-FR")} {suffix}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* =========================================================================
   TAB 2 — Objectifs (jauges)
   ========================================================================= */

function ObjectivesTab() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ types: [], scopes: [] });
  const [links, setLinks] = useState({ companies: [], buildings: [], lots: [] });
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState({
    type: "cashflow_monthly", scope: "organization", ref_id: "",
    target_value: 0, tolerance_pct: 10, label: "", notes: "",
  });

  const load = async () => {
    const { data } = await api.get("/command-center/objectives");
    setItems(data.items);
  };
  useEffect(() => {
    load();
    api.get("/command-center/objectives/meta").then(({ data }) => setMeta(data));
    Promise.all([api.get("/companies"), api.get("/buildings"), api.get("/lots")])
      .then(([c, b, l]) => setLinks({
        companies: c.data.items || [], buildings: b.data.items || [], lots: l.data.items || [],
      }));
  }, []);

  const submit = async () => {
    try {
      await api.post("/command-center/objectives", {
        ...form,
        ref_id: form.scope === "organization" ? null : form.ref_id || null,
        target_value: Number(form.target_value),
        tolerance_pct: Number(form.tolerance_pct),
      });
      toast.success("Objectif créé");
      setOpenNew(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet objectif ?")) return;
    await api.delete(`/command-center/objectives/${id}`);
    toast.success("Objectif supprimé");
    load();
  };

  const refOptions = form.scope === "company" ? links.companies :
                     form.scope === "building" ? links.buildings :
                     form.scope === "lot" ? links.lots : [];

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={openNew} onOpenChange={setOpenNew}>
          <DialogTrigger asChild>
            <Button className="btn-pill" data-testid="obj-new-btn">
              <Target className="mr-2 h-3 w-3" />Nouvel objectif
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Définir un objectif patrimonial</DialogTitle>
              <DialogDescription>
                Fixez une cible chiffrée. L&apos;application suit automatiquement votre progression.
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type d&apos;objectif</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="obj-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {meta.types.map((t) => (
                      <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Périmètre</Label>
                <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v, ref_id: "" })}>
                  <SelectTrigger data-testid="obj-scope"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="organization">Toute l&apos;organisation</SelectItem>
                    <SelectItem value="company">Une société</SelectItem>
                    <SelectItem value="building">Un immeuble</SelectItem>
                    <SelectItem value="lot">Un lot</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.scope !== "organization" && (
                <div className="col-span-2">
                  <Label>Cible du périmètre</Label>
                  <Select value={form.ref_id || "__none"} onValueChange={(v) => setForm({ ...form, ref_id: v === "__none" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">—</SelectItem>
                      {refOptions.map((o) => (
                        <SelectItem key={o.id} value={o.id}>{o.denomination || o.name || o.reference}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Valeur cible</Label>
                <Input type="number" step="0.01" value={form.target_value}
                       onChange={(e) => setForm({ ...form, target_value: e.target.value })}
                       data-testid="obj-target" />
              </div>
              <div>
                <Label>Tolérance %</Label>
                <Input type="number" step="1" value={form.tolerance_pct}
                       onChange={(e) => setForm({ ...form, tolerance_pct: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label>Libellé</Label>
                <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                       placeholder="Ex : Cash-flow annuel visé pour 2026" />
              </div>
              <div className="col-span-2">
                <Label>Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpenNew(false)}>Annuler</Button>
              <Button onClick={submit} data-testid="obj-save">Créer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length === 0 && (
          <div className="card-elevated p-12 text-center text-muted-foreground col-span-full">
            <Target className="mx-auto h-8 w-8 opacity-60 mb-3" />
            Aucun objectif. Cliquez sur « Nouvel objectif » pour démarrer.
          </div>
        )}
        {items.map((o) => {
          const typeDef = meta.types.find((t) => t.code === o.type);
          const unit = typeDef?.unit || "EUR";
          return (
            <div key={o.id} className="card-elevated p-5 space-y-3" data-testid={`obj-card-${o.id}`}>
              <div className="flex justify-between items-start">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    {typeDef?.label}
                  </div>
                  <div className="font-heading text-base mt-1">
                    {o.label || (unit === "EUR" ? eur(o.target_value) : pct(o.target_value))}
                  </div>
                  <div className="text-xs text-muted-foreground">{o.scope}</div>
                </div>
                <button onClick={() => remove(o.id)} className="text-muted-foreground hover:text-red-600" data-testid={`obj-del-${o.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="flex justify-between text-sm">
                <span>Actuel</span>
                <span className="font-mono font-semibold">
                  {unit === "EUR" ? eur(o.actual_value) : pct(o.actual_value)}
                </span>
              </div>
              <Gauge3D value={o.progress_pct} target={o.target_value} unit={unit} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* =========================================================================
   TAB 3 — Alertes
   ========================================================================= */

function AlertsTab() {
  const [alerts, setAlerts] = useState([]);
  const [rules, setRules] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [view, setView] = useState("active"); // active | rules

  const load = async () => {
    const [a, r] = await Promise.all([
      api.get("/command-center/alerts"),
      api.get("/command-center/alerts/rules"),
    ]);
    setAlerts(a.data.items);
    setRules(r.data.rules);
  };
  useEffect(() => { load(); }, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/command-center/alerts/refresh");
      toast.success(`${data.generated} alerte(s) mise(s) à jour`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setRefreshing(false); }
  };

  const dismiss = async (id) => {
    await api.post(`/command-center/alerts/${id}/dismiss`);
    toast.success("Alerte fermée");
    load();
  };

  const toggleRule = async (code, enabled) => {
    await api.patch(`/command-center/alerts/rules/${code}`, { enabled });
    load();
  };

  const bySev = useMemo(() => ({
    critical: alerts.filter((a) => a.severity === "critical"),
    warning: alerts.filter((a) => a.severity === "warning"),
    info: alerts.filter((a) => a.severity === "info"),
  }), [alerts]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-between gap-2 items-center">
        <div className="flex gap-2">
          <Button variant={view === "active" ? "default" : "outline"} onClick={() => setView("active")}
                  data-testid="alerts-view-active">
            Alertes actives ({alerts.length})
          </Button>
          <Button variant={view === "rules" ? "default" : "outline"} onClick={() => setView("rules")}
                  data-testid="alerts-view-rules">
            Règles ({rules.length})
          </Button>
        </div>
        {view === "active" && (
          <Button onClick={refresh} disabled={refreshing} data-testid="alerts-refresh">
            <RefreshCw className={`h-3 w-3 mr-1 ${refreshing ? "animate-spin" : ""}`} />
            Rafraîchir
          </Button>
        )}
      </div>
      {view === "active" && (
        <div className="space-y-4">
          {["critical", "warning", "info"].map((sev) => bySev[sev].length > 0 && (
            <section key={sev}>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-2">
                <StatusBadge tone={SEV_TONE[sev]}>{sev === "critical" ? "Critique" : sev === "warning" ? "Vigilance" : "Info"}</StatusBadge>
                {bySev[sev].length}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {bySev[sev].map((a) => (
                  <div key={a.id} className="card-elevated p-4" data-testid={`alert-${a.id}`}>
                    <div className="flex justify-between items-start gap-2">
                      <div className="min-w-0">
                        <div className="font-heading text-sm">{a.title}</div>
                        <div className="text-xs text-muted-foreground mt-1">{a.message}</div>
                        <div className="text-[10px] text-muted-foreground mt-1">Règle : {a.rule_code}</div>
                      </div>
                      <button onClick={() => dismiss(a.id)} className="text-muted-foreground hover:text-red-600" data-testid={`alert-dismiss-${a.id}`}>
                        <XCircle className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
          {alerts.length === 0 && (
            <div className="card-elevated p-12 text-center text-muted-foreground">
              <CheckCircle2 className="mx-auto h-8 w-8 opacity-60 text-emerald-500 mb-3" />
              Aucune alerte active. Cliquez sur « Rafraîchir » pour lancer une analyse.
            </div>
          )}
        </div>
      )}
      {view === "rules" && (
        <div className="space-y-2">
          {rules.map((r) => (
            <div key={r.code} className="card-elevated p-4 flex justify-between items-center gap-3">
              <div className="min-w-0">
                <div className="font-medium text-sm">{r.label}</div>
                <div className="text-xs text-muted-foreground">
                  code: {r.code} · sévérité: {r.severity} · params: {JSON.stringify(r.params)}
                </div>
              </div>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={r.enabled}
                       onChange={(e) => toggleRule(r.code, e.target.checked)}
                       data-testid={`rule-toggle-${r.code}`}
                       className="h-4 w-4" />
                <span className="text-xs">{r.enabled ? "Active" : "Inactive"}</span>
              </label>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* =========================================================================
   TAB 4 — Santé patrimoniale
   ========================================================================= */

function HealthTab() {
  const [data, setData] = useState(null);
  const [weights, setWeights] = useState(null);
  const [scope, setScope] = useState("organization");
  const [refId, setRefId] = useState("");
  const [links, setLinks] = useState({ companies: [], buildings: [], lots: [] });

  const load = async () => {
    const params = { scope };
    if (refId) params.ref_id = refId;
    const { data } = await api.get("/command-center/health-score", { params });
    setData(data);
  };
  useEffect(() => { load();  }, [scope, refId]);
  useEffect(() => {
    api.get("/command-center/health-score/weights").then(({ data }) => setWeights(data.weights));
    Promise.all([api.get("/companies"), api.get("/buildings"), api.get("/lots")])
      .then(([c, b, l]) => setLinks({
        companies: c.data.items || [], buildings: b.data.items || [], lots: l.data.items || [],
      }));
  }, []);

  const saveWeights = async () => {
    const total = Object.values(weights || {}).reduce((a, b) => a + Number(b || 0), 0);
    if (total !== 100) { toast.error(`La somme doit faire 100 (actuel : ${total})`); return; }
    try {
      await api.patch("/command-center/health-score/weights", weights);
      toast.success("Pondération enregistrée");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const refOptions = scope === "company" ? links.companies :
                     scope === "building" ? links.buildings :
                     scope === "lot" ? links.lots : [];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
        <div>
          <Label className="text-xs">Périmètre</Label>
          <Select value={scope} onValueChange={(v) => { setScope(v); setRefId(""); }}>
            <SelectTrigger data-testid="health-scope"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="organization">Toute l&apos;organisation</SelectItem>
              <SelectItem value="company">Une société</SelectItem>
              <SelectItem value="building">Un immeuble</SelectItem>
              <SelectItem value="lot">Un lot</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {scope !== "organization" && (
          <div>
            <Label className="text-xs">Sélection</Label>
            <Select value={refId || "__none"} onValueChange={(v) => setRefId(v === "__none" ? "" : v)}>
              <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">—</SelectItem>
                {refOptions.map((o) => (
                  <SelectItem key={o.id} value={o.id}>{o.denomination || o.name || o.reference}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="card-elevated p-6 text-center col-span-1 flex flex-col justify-center">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Score de santé</div>
            <div className="mt-3 flex items-baseline justify-center gap-2">
              <div className="text-6xl font-bold text-primary" data-testid="health-score-value">{data.score}</div>
              <div className="text-xl text-muted-foreground">/ 100</div>
            </div>
            <div className="mt-2">
              <StatusBadge tone={data.score >= 70 ? "success" : data.score >= 55 ? "warning" : "danger"}>
                Grade {data.grade}
              </StatusBadge>
            </div>
          </div>
          <div className="card-elevated p-6 col-span-2">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-4">Décomposition</div>
            <div className="space-y-3">
              {Object.entries(data.breakdown || {}).map(([k, v]) => (
                <div key={k} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="capitalize">{k}</span>
                    <span className="font-mono">{v} / 100 · pondération {data.weights[k]}%</span>
                  </div>
                  <div className="h-2 bg-secondary rounded overflow-hidden">
                    <div className={`h-full ${v >= 70 ? "bg-emerald-500" : v >= 50 ? "bg-amber-400" : "bg-red-400"}`}
                         style={{ width: `${v}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {weights && (
        <div className="card-elevated p-5">
          <div className="font-heading text-base mb-3 flex items-center gap-2">
            <Gauge className="h-4 w-4" />Pondération du score (somme = 100)
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.keys(weights).map((k) => (
              <div key={k}>
                <Label className="text-xs capitalize">{k}</Label>
                <Input type="number" min="0" max="100" value={weights[k]}
                       onChange={(e) => setWeights({ ...weights, [k]: Number(e.target.value) })}
                       data-testid={`weight-${k}`} />
              </div>
            ))}
          </div>
          <div className="text-xs text-muted-foreground mt-2">
            Total actuel : {Object.values(weights).reduce((a, b) => a + Number(b || 0), 0)}
          </div>
          <div className="mt-3 flex justify-end">
            <Button onClick={saveWeights} data-testid="weights-save">Enregistrer</Button>
          </div>
        </div>
      )}
    </div>
  );
}

/* =========================================================================
   TAB 5 — Conseiller (règles, sans IA générative)
   ========================================================================= */

function AdvisorTab() {
  const [recs, setRecs] = useState([]);
  const load = async () => {
    const { data } = await api.get("/command-center/advisor/recommendations");
    setRecs(data.items);
  };
  useEffect(() => { load(); }, []);

  const dismiss = async (key) => {
    await api.post(`/command-center/advisor/dismiss/${encodeURIComponent(key)}`);
    toast.success("Recommandation masquée");
    load();
  };

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-dashed border-primary/30 bg-primary/5 p-4 text-sm text-primary/80 flex items-start gap-2">
        <Lightbulb className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <div>
          Le conseiller applique des <strong>règles patrimoniales</strong> pour signaler des points à vérifier.
          Chaque recommandation est une <em>suggestion</em>, jamais une certitude — validez toujours avec votre conseil ou expert-comptable.
        </div>
      </div>
      {recs.length === 0 && (
        <div className="card-elevated p-12 text-center text-muted-foreground">
          <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-500 mb-3" />
          Aucune recommandation active. Excellent !
        </div>
      )}
      {recs.map((r) => (
        <div key={r.key} className="card-elevated p-5" data-testid={`reco-${r.key}`}>
          <div className="flex justify-between items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <StatusBadge tone={PRIO_TONE[r.priority]}>{PRIO_LABEL[r.priority]}</StatusBadge>
                <span className="text-xs text-muted-foreground">{r.kind}</span>
              </div>
              <div className="font-heading text-base">{r.title}</div>
              <div className="text-sm text-muted-foreground mt-1">{r.message}</div>
              {(r.suggested_actions || []).length > 0 && (
                <div className="mt-2">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Actions suggérées</div>
                  <ul className="text-sm list-disc list-inside space-y-0.5">
                    {r.suggested_actions.map((a, i) => <li key={`action-${i}-${(a || "").slice(0, 20)}`}>{a}</li>)}
                  </ul>
                </div>
              )}
            </div>
            <Button variant="ghost" size="sm" onClick={() => dismiss(r.key)} data-testid={`reco-dismiss-${r.key}`}>
              Masquer
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

/* =========================================================================
   TAB 6 — Historique
   ========================================================================= */

function HistoryTab() {
  const [period, setPeriod] = useState("monthly");
  const [months, setMonths] = useState(24);
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/command-center/history", { params: { period, months } })
      .then(({ data }) => setData(data));
  }, [period, months]);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <Label className="text-xs">Granularité</Label>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40" data-testid="hist-period"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="monthly">Mensuel</SelectItem>
              <SelectItem value="quarterly">Trimestriel</SelectItem>
              <SelectItem value="annual">Annuel</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Nombre de périodes</Label>
          <Input type="number" min="3" max="60" value={months}
                 onChange={(e) => setMonths(Number(e.target.value) || 24)}
                 className="w-24" />
        </div>
      </div>
      {data && (
        <div className="card-elevated overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 text-xs uppercase text-muted-foreground">
              <tr className="text-left"><th className="px-3 py-2">Période</th><th className="px-3 py-2 text-right">Loyers</th><th className="px-3 py-2 text-right">Charges</th><th className="px-3 py-2 text-right">Delta</th></tr>
            </thead>
            <tbody className="divide-y">
              {data.series.map((s) => (
                <tr key={s.period}>
                  <td className="px-3 py-2 font-mono">{s.period}</td>
                  <td className="px-3 py-2 text-right font-mono">{eur(s.rents)}</td>
                  <td className="px-3 py-2 text-right font-mono">{eur(s.charges)}</td>
                  <td className={`px-3 py-2 text-right font-mono ${s.delta >= 0 ? "text-emerald-700" : "text-red-600"}`}>{eur(s.delta)}</td>
                </tr>
              ))}
              {data.series.length === 0 && (
                <tr><td colSpan={4} className="px-3 py-12 text-center text-muted-foreground">Aucune donnée sur cette période.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* =========================================================================
   TAB 7 — Comparer
   ========================================================================= */

function CompareTab() {
  const [kind, setKind] = useState("buildings");
  const [left, setLeft] = useState({ id: "", start: "", end: "" });
  const [right, setRight] = useState({ id: "", start: "", end: "" });
  const [links, setLinks] = useState({ companies: [], buildings: [] });
  const [result, setResult] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/companies"), api.get("/buildings")])
      .then(([c, b]) => setLinks({
        companies: c.data.items || [], buildings: b.data.items || [],
      }));
  }, []);

  const run = async () => {
    try {
      const { data } = await api.post("/command-center/compare", {
        kind, left, right,
      });
      setResult(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const opts = kind === "buildings" ? links.buildings : links.companies;

  return (
    <div className="space-y-4">
      <div className="card-elevated p-5 space-y-3">
        <div className="flex flex-wrap gap-3">
          <div>
            <Label className="text-xs">Comparer</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger className="w-52" data-testid="cmp-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="buildings">Deux immeubles</SelectItem>
                <SelectItem value="companies">Deux sociétés / SCI</SelectItem>
                <SelectItem value="periods">Deux périodes</SelectItem>
                <SelectItem value="years">Deux années</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {(kind === "buildings" || kind === "companies") && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>{kind === "buildings" ? "Immeuble A" : "Société A"}</Label>
              <Select value={left.id || "__none"} onValueChange={(v) => setLeft({ ...left, id: v === "__none" ? "" : v })}>
                <SelectTrigger data-testid="cmp-left"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">—</SelectItem>
                  {opts.map((o) => <SelectItem key={o.id} value={o.id}>{o.denomination || o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{kind === "buildings" ? "Immeuble B" : "Société B"}</Label>
              <Select value={right.id || "__none"} onValueChange={(v) => setRight({ ...right, id: v === "__none" ? "" : v })}>
                <SelectTrigger data-testid="cmp-right"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">—</SelectItem>
                  {opts.map((o) => <SelectItem key={o.id} value={o.id}>{o.denomination || o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
        {(kind === "periods" || kind === "years") && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Période A</div>
              <div className="grid grid-cols-2 gap-2">
                <Input type="date" value={left.start} onChange={(e) => setLeft({ ...left, start: e.target.value })} />
                <Input type="date" value={left.end} onChange={(e) => setLeft({ ...left, end: e.target.value })} />
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Période B</div>
              <div className="grid grid-cols-2 gap-2">
                <Input type="date" value={right.start} onChange={(e) => setRight({ ...right, start: e.target.value })} />
                <Input type="date" value={right.end} onChange={(e) => setRight({ ...right, end: e.target.value })} />
              </div>
            </div>
          </div>
        )}
        <div className="flex justify-end">
          <Button onClick={run} data-testid="cmp-run">Comparer</Button>
        </div>
      </div>
      {result && <CompareResult result={result} />}
    </div>
  );
}

function CompareResult({ result }) {
  if (result.kind === "buildings") {
    const rows = [
      { k: "Valeur", l: result.left.value.current_value, r: result.right.value.current_value, unit: "EUR" },
      { k: "Cash-flow mensuel", l: result.left.cashflow.monthly, r: result.right.cashflow.monthly, unit: "EUR" },
      { k: "Cash-flow annuel", l: result.left.cashflow.annual, r: result.right.cashflow.annual, unit: "EUR" },
      { k: "Rendement brut", l: result.left.yields_pct.gross, r: result.right.yields_pct.gross, unit: "PCT" },
      { k: "Rendement net", l: result.left.yields_pct.net, r: result.right.yields_pct.net, unit: "PCT" },
      { k: "CRD", l: result.left.credit.remaining_principal, r: result.right.credit.remaining_principal, unit: "EUR" },
    ];
    return (
      <div className="card-elevated overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-xs uppercase text-muted-foreground">
            <tr className="text-left"><th className="px-3 py-2">Indicateur</th><th className="px-3 py-2 text-right">{result.left.building_name}</th><th className="px-3 py-2 text-right">{result.right.building_name}</th><th className="px-3 py-2 text-right">Écart</th></tr>
          </thead>
          <tbody className="divide-y">
            {rows.map((row) => {
              const delta = row.r - row.l;
              const fmt = row.unit === "EUR" ? eur : pct;
              return (
                <tr key={row.k}>
                  <td className="px-3 py-2 font-medium">{row.k}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmt(row.l)}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmt(row.r)}</td>
                  <td className={`px-3 py-2 text-right font-mono ${delta >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                    {delta >= 0 ? <ArrowUp className="inline h-3 w-3" /> : <ArrowDown className="inline h-3 w-3" />}
                    {fmt(Math.abs(delta))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }
  if (result.kind === "periods" || result.kind === "years") {
    return (
      <div className="card-elevated overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-xs uppercase text-muted-foreground">
            <tr className="text-left"><th className="px-3 py-2">Indicateur</th><th className="px-3 py-2 text-right">A ({result.left.period.start} → {result.left.period.end})</th><th className="px-3 py-2 text-right">B ({result.right.period.start} → {result.right.period.end})</th><th className="px-3 py-2 text-right">Écart</th></tr>
          </thead>
          <tbody className="divide-y">
            {["rents", "charges", "delta"].map((k) => (
              <tr key={k}>
                <td className="px-3 py-2 capitalize font-medium">{k}</td>
                <td className="px-3 py-2 text-right font-mono">{eur(result.left[k])}</td>
                <td className="px-3 py-2 text-right font-mono">{eur(result.right[k])}</td>
                <td className={`px-3 py-2 text-right font-mono ${result.delta[k] >= 0 ? "text-emerald-700" : "text-red-600"}`}>{eur(result.delta[k])}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  return <div className="card-elevated p-6">Résultat : {JSON.stringify(result).slice(0, 400)}…</div>;
}

/* =========================================================================
   TAB 8 — Export
   ========================================================================= */

function ExportTab() {
  const [report, setReport] = useState("overview");
  const [busy, setBusy] = useState(null);
  const download = async (fmt) => {
    setBusy(fmt);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/command-center/export/${fmt}?report=${report}`;
      const resp = await fetch(url, { credentials: "include" });
      if (!resp.ok) throw new Error(await resp.text());
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `fonbex_${report}.${fmt}`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`Export ${fmt.toUpperCase()} téléchargé`);
    } catch (e) { toast.error(String(e).slice(0, 200)); }
    finally { setBusy(null); }
  };
  return (
    <div className="space-y-5 max-w-3xl">
      <div className="card-elevated p-5">
        <div className="font-heading text-base mb-3">Rapport</div>
        <Select value={report} onValueChange={setReport}>
          <SelectTrigger data-testid="export-report"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="overview">Vue d&apos;ensemble (tous indicateurs)</SelectItem>
            <SelectItem value="banque">Banque (financement + endettement)</SelectItem>
            <SelectItem value="expert_comptable">Expert-comptable (résultats + charges)</SelectItem>
            <SelectItem value="associe">Associé (patrimoine + rendements)</SelectItem>
            <SelectItem value="investisseur">Investisseur (rentabilité + valorisation)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <button onClick={() => download("csv")} disabled={busy !== null}
                className="card-elevated p-5 hover:shadow-lg text-left" data-testid="export-csv">
          <FileText className="h-6 w-6 mb-2 text-primary" />
          <div className="font-heading text-base">CSV</div>
          <div className="text-xs text-muted-foreground mt-1">Fichier délimité — Excel, Google Sheets, Numbers</div>
          {busy === "csv" && <div className="text-xs mt-2">Téléchargement…</div>}
        </button>
        <button onClick={() => download("xlsx")} disabled={busy !== null}
                className="card-elevated p-5 hover:shadow-lg text-left" data-testid="export-xlsx">
          <FileSpreadsheet className="h-6 w-6 mb-2 text-primary" />
          <div className="font-heading text-base">Excel</div>
          <div className="text-xs text-muted-foreground mt-1">Classeur .xlsx avec deux feuilles (KPIs + détail)</div>
          {busy === "xlsx" && <div className="text-xs mt-2">Téléchargement…</div>}
        </button>
        <button onClick={() => download("pdf")} disabled={busy !== null}
                className="card-elevated p-5 hover:shadow-lg text-left" data-testid="export-pdf">
          <FileText className="h-6 w-6 mb-2 text-primary" />
          <div className="font-heading text-base">PDF</div>
          <div className="text-xs text-muted-foreground mt-1">Rapport imprimable au format A4 paysage</div>
          {busy === "pdf" && <div className="text-xs mt-2">Téléchargement…</div>}
        </button>
      </div>
      <div className="text-xs text-muted-foreground">
        Les rapports incluent tous les indicateurs de la vue d&apos;ensemble + le détail par immeuble adapté au destinataire.
      </div>
    </div>
  );
}
